import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Alert,
  AlertIcon,
  Tooltip,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FiRefreshCw } from "react-icons/fi";
import { resourceService, projectService } from "../services/edosService";

export default function ResourcesPage() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const headerBg = useColorModeValue("gray.50", "gray.700");

  const [projects, setProjects] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [utilization, setUtilization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [filterProjectId, setFilterProjectId] = useState("");
  const [form, setForm] = useState({
    employeeId: "",
    projectId: "",
    allocatedHours: "",
    startDate: "",
    endDate: "",
    allocationPercentage: "",
    role: "",
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch (_) {}
  }, []);

  const fetchAllocations = useCallback(async () => {
    if (!filterProjectId) return;
    setLoading(true);
    try {
      const res = await resourceService.listProjectAllocations(filterProjectId);
      setAllocations(res.data.data || res.data.allocations || []);
    } catch (err) {
      toast({ title: "Failed to load allocations", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [filterProjectId, toast]);

  const fetchUtilization = useCallback(async () => {
    setLoading(true);
    try {
      const res = await resourceService.utilization();
      setUtilization(res.data.data || res.data);
    } catch (err) {
      toast({ title: "Failed to load utilization", status: "error" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (tabIndex === 0) fetchAllocations();
    else if (tabIndex === 1) fetchUtilization();
  }, [tabIndex, fetchAllocations, fetchUtilization]);

  const handleAllocate = async () => {
    if (
      !form.employeeId ||
      !form.projectId ||
      !form.allocatedHours ||
      !form.startDate ||
      !form.endDate
    ) {
      toast({
        title: "Employee, project, hours, start and end dates are required",
        status: "warning",
      });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, allocatedHours: Number(form.allocatedHours) };
      if (form.allocationPercentage)
        payload.allocationPercentage = Number(form.allocationPercentage);
      await resourceService.createAllocation(payload);
      toast({ title: "Resource allocated", status: "success" });
      onClose();
      setForm({
        employeeId: "",
        projectId: filterProjectId,
        allocatedHours: "",
        startDate: "",
        endDate: "",
        allocationPercentage: "",
        role: "",
      });
      fetchAllocations();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to allocate",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this resource allocation?")) return;
    try {
      await resourceService.deactivateAllocation(id);
      toast({ title: "Allocation removed", status: "success" });
      fetchAllocations();
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed",
        status: "error",
      });
    }
  };

  return (
    <Box p={6}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Text fontSize="2xl" fontWeight="bold">
            Resource Management
          </Text>
          <Text color="gray.500" fontSize="sm">
            Allocate and track team resources across projects
          </Text>
        </Box>
        <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            aria-label="Refresh"
            onClick={tabIndex === 0 ? fetchAllocations : fetchUtilization}
          />
          <Button leftIcon={<AddIcon />} colorScheme="purple" onClick={onOpen}>
            Allocate Resource
          </Button>
        </HStack>
      </Flex>

      <Tabs
        onChange={(i) => {
          setTabIndex(i);
        }}
      >
        <TabList mb={4}>
          <Tab>Allocations</Tab>
          <Tab>Utilization Report</Tab>
        </TabList>
        <TabPanels>
          {/* Allocations */}
          <TabPanel px={0}>
            <Flex gap={3} mb={4} align="center">
              <Text fontSize="sm" fontWeight="medium" whiteSpace="nowrap">
                Filter by Project:
              </Text>
              <Select
                placeholder="All projects..."
                value={filterProjectId}
                onChange={(e) => {
                  setFilterProjectId(e.target.value);
                  setForm((f) => ({ ...f, projectId: e.target.value }));
                }}
                maxW="320px"
              >
                {projects.map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.projectName}
                  </option>
                ))}
              </Select>
            </Flex>
            {!filterProjectId ? (
              <Alert status="info">
                <AlertIcon />
                Select a project to view allocations.
              </Alert>
            ) : (
              <Box
                bg={bg}
                border="1px"
                borderColor={borderColor}
                borderRadius="md"
                overflow="hidden"
              >
                {loading ? (
                  <Flex justify="center" p={10}>
                    <Spinner size="xl" color="purple.500" />
                  </Flex>
                ) : allocations.length === 0 ? (
                  <Alert status="info" m={4}>
                    <AlertIcon />
                    No resource allocations.
                  </Alert>
                ) : (
                  <Table variant="simple" size="sm">
                    <Thead bg={headerBg}>
                      <Tr>
                        <Th>Employee</Th>
                        <Th>Role</Th>
                        <Th>Allocated Hours</Th>
                        <Th>% Allocation</Th>
                        <Th>Start</Th>
                        <Th>End</Th>
                        <Th>Actions</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {allocations.map((a) => (
                        <Tr key={a._id}>
                          <Td>
                            <Text fontSize="sm">
                              {a.employeeId?.name ||
                                a.employeeId?.email ||
                                a.employeeId ||
                                "-"}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm">{a.role || "-"}</Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm">{a.allocatedHours}h</Text>
                          </Td>
                          <Td>
                            {a.allocationPercentage ? (
                              <VStack align="start" spacing={0}>
                                <Text fontSize="xs">
                                  {a.allocationPercentage}%
                                </Text>
                                <Progress
                                  value={a.allocationPercentage}
                                  size="xs"
                                  colorScheme="purple"
                                  w="80px"
                                />
                              </VStack>
                            ) : (
                              "-"
                            )}
                          </Td>
                          <Td>
                            <Text fontSize="sm">
                              {a.startDate
                                ? new Date(a.startDate).toLocaleDateString()
                                : "-"}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm">
                              {a.endDate
                                ? new Date(a.endDate).toLocaleDateString()
                                : "-"}
                            </Text>
                          </Td>
                          <Td>
                            <Tooltip label="Remove Allocation">
                              <IconButton
                                size="xs"
                                icon={<DeleteIcon />}
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => handleDelete(a._id)}
                              />
                            </Tooltip>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </Box>
            )}
          </TabPanel>

          {/* Utilization Report */}
          <TabPanel px={0}>
            {loading ? (
              <Flex justify="center" p={10}>
                <Spinner />
              </Flex>
            ) : utilization ? (
              <VStack spacing={4} align="stretch">
                {Array.isArray(utilization) ? (
                  <Box
                    bg={bg}
                    border="1px"
                    borderColor={borderColor}
                    borderRadius="md"
                    overflow="hidden"
                  >
                    <Table variant="simple" size="sm">
                      <Thead bg={headerBg}>
                        <Tr>
                          <Th>Employee</Th>
                          <Th>Allocated Hours</Th>
                          <Th>Active Projects</Th>
                          <Th>Utilization %</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {utilization.map((u, i) => (
                          <Tr key={i}>
                            <Td>
                              <Text fontSize="sm">
                                {u.employeeName || u.employee || "-"}
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm">
                                {u.totalAllocatedHours || 0}h
                              </Text>
                            </Td>
                            <Td>
                              <Text fontSize="sm">{u.activeProjects || 0}</Text>
                            </Td>
                            <Td>
                              <VStack align="start" spacing={0}>
                                <Text fontSize="xs">
                                  {u.utilizationPercentage || 0}%
                                </Text>
                                <Progress
                                  value={u.utilizationPercentage || 0}
                                  size="xs"
                                  colorScheme={
                                    u.utilizationPercentage > 100
                                      ? "red"
                                      : "purple"
                                  }
                                  w="100px"
                                />
                              </VStack>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                    {Object.entries(utilization)
                      .filter(([, v]) => typeof v !== "object")
                      .map(([k, v]) => (
                        <Box
                          key={k}
                          bg={bg}
                          border="1px"
                          borderColor={borderColor}
                          borderRadius="md"
                          p={4}
                        >
                          <Stat>
                            <StatLabel
                              color="gray.500"
                              textTransform="capitalize"
                            >
                              {k.replace(/([A-Z])/g, " $1")}
                            </StatLabel>
                            <StatNumber fontSize="xl">{String(v)}</StatNumber>
                          </Stat>
                        </Box>
                      ))}
                  </SimpleGrid>
                )}
              </VStack>
            ) : (
              <Alert status="info">
                <AlertIcon />
                No utilization data available.
              </Alert>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Allocate Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Allocate Resource</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Employee ID</FormLabel>
                <Input
                  value={form.employeeId}
                  onChange={(e) =>
                    setForm({ ...form, employeeId: e.target.value })
                  }
                  placeholder="Employee ObjectId"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Project</FormLabel>
                <Select
                  value={form.projectId}
                  onChange={(e) =>
                    setForm({ ...form, projectId: e.target.value })
                  }
                >
                  <option value="">Select project...</option>
                  {projects.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.projectName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <SimpleGrid columns={2} spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Allocated Hours</FormLabel>
                  <Input
                    type="number"
                    value={form.allocatedHours}
                    onChange={(e) =>
                      setForm({ ...form, allocatedHours: e.target.value })
                    }
                    placeholder="e.g. 40"
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Allocation %</FormLabel>
                  <Input
                    type="number"
                    value={form.allocationPercentage}
                    onChange={(e) =>
                      setForm({ ...form, allocationPercentage: e.target.value })
                    }
                    placeholder="e.g. 50"
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Start Date</FormLabel>
                  <Input
                    type="date"
                    value={form.startDate}
                    onChange={(e) =>
                      setForm({ ...form, startDate: e.target.value })
                    }
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>End Date</FormLabel>
                  <Input
                    type="date"
                    value={form.endDate}
                    onChange={(e) =>
                      setForm({ ...form, endDate: e.target.value })
                    }
                  />
                </FormControl>
              </SimpleGrid>
              <FormControl>
                <FormLabel>Role</FormLabel>
                <Input
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="e.g. Backend Developer"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              isLoading={submitting}
              onClick={handleAllocate}
            >
              Allocate
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
